import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import { useT, useLang } from '../lib/i18n.jsx';
import { useTheme } from '../lib/theme.jsx';
import { errorMessage } from '../lib/errors.js';
import { LangToggle } from '../components/Topbar.jsx';

export default function LoginPage() {
  const { user, login, verifyMfa } = useAuth();
  const t = useT();
  const { lang, setLang } = useLang();
  const { dark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (user) {
    const from = location.state?.from || '/';
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      if (mfaToken) {
        await verifyMfa(mfaToken, mfaCode);
      } else {
        const res = await login(email, password);
        if (res?.mfaToken) {
          setMfaToken(res.mfaToken);
          return;
        }
      }
      navigate(location.state?.from || '/', { replace: true });
    } catch (e2) {
      setErr(errorMessage(e2, { lang, context: mfaToken ? 'mfa' : 'login' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-shell" data-theme={dark ? 'dark' : 'light'}>
      <div className="login-card">
        <div className="login-top">
          <LangToggle lang={lang} setLang={setLang} />
        </div>
        <div className="login-brand">
          <div className="mark">D</div>
          <div className="name">
            DMRS<span>Console</span>
          </div>
        </div>
        <h1 className="login-title">{t('login.title')}</h1>
        <p className="login-sub">
          {t('login.subtitle')}
        </p>

        {err && <div className="login-error" role="alert">{err}</div>}

        <form className="login-form" onSubmit={submit}>
          {!mfaToken && (
            <>
              <div>
                <label htmlFor="email">{t('account.profile.email')}</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="pw">{t('account.password')}</label>
                <input
                  id="pw"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </>
          )}
          {mfaToken && (
            <div>
              <label htmlFor="mfa">{t('login.mfaCode')}</label>
              <input
                id="mfa"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
              />
            </div>
          )}
          <div className="login-actions">
            {mfaToken ? (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setMfaToken(null);
                  setMfaCode('');
                  setErr('');
                }}
              >
                {t('login.back')}
              </button>
            ) : (
              <Link className="login-link" to="/reset-password">{t('login.forgot')}</Link>
            )}
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? t('account.working') : t('login.submit')}
            </button>
          </div>
        </form>

        <div className="login-foot">
          {t('login.noAccount')}
        </div>
      </div>
    </div>
  );
}
